import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type {
  ChatMentionsMenuItemType,
  ChatRef,
  MentionEntityWithOffset,
} from '@terra/ui-design-system';
import {
  extractPendingAskUserInteraction,
  toasterApi,
} from '@terra/ui-design-system';
import {
  discardCachedCopilotPromoteResponse,
  forgetBusyCopilotFollowUp,
  forgetUserMessagePlacement,
  prepareStreamingCopilotFollowUpSend,
  rememberBusyCopilotFollowUp,
  rememberUserMessagePlacement,
  userMessagePlacementForSend,
} from '../store/copilot-pending-follow-up';
import { metadataForOutgoingUserMessage } from '../store/copilot-outgoing-user-message';
import { releaseOpenCopilotStream } from '../store/resume-background-turn';
import {
  getCopilotInvokeBodyExtras,
  useChatInstancesStore,
} from '../store/chatInstancesStore';
import { v4 as uuidv4 } from 'uuid';
import { prepareChatUserTurn } from './prepare-chat-user-turn';
import {
  isActiveChatInstanceStatus,
  isRecoverableStreamError,
} from '../utils/chatNetworkErrors';
import {
  buildChatInstanceOutgoingParts,
  type BranchEditSource,
} from './useChatInstanceMentionBuilder';

export async function performChatInstanceSendMessage({
  message,
  branchFromMessageId,
  activeBranchEditSource,
  inlinePositionedMentions,
  chatId,
  isApplyingBranch,
  isChatWindowActive,
  selectedMentions,
  clearMentions,
  getMentionsFromEditor,
  chatComponentRef,
  updateChatTab,
  skillDraftId,
  linkedObject,
  tCopilot,
  chatMessages,
  displayedMessages,
  status,
  editingQueuedMessageId,
  updateQueuedMessage,
  clearBlockedAutoDrainForQueueItem,
  onQueuedMessageEditComplete,
  hasPendingHITLReview,
  enqueueMessage,
  handleAbort,
  setBusyFollowUps,
  pauseQueueDrainAfterAbortRef,
  cancelScheduledRecovery,
  intentionalAbortRef,
  getChatExecuteUrl,
  sendUserMessageWithResult,
  sendErrorRef,
  streamError,
  clearNeoResumeSuppression,
  clearComposerLoadingSuppression,
}: {
  message: TerraUIMessage;
  branchFromMessageId?: string;
  activeBranchEditSource?: NonNullable<BranchEditSource>;
  inlinePositionedMentions?: MentionEntityWithOffset[];
  chatId: string;
  isApplyingBranch: boolean;
  isChatWindowActive: boolean;
  selectedMentions: ChatMentionsMenuItemType[] | undefined;
  clearMentions: () => void;
  getMentionsFromEditor: () => MentionEntityWithOffset[];
  chatComponentRef?: React.RefObject<ChatRef | null>;
  updateChatTab: (
    tabId: string,
    updates: {
      mentions?: ChatMentionsMenuItemType[];
      linkedObject?: { kind: string; id: string; label?: string };
    },
  ) => void;
  skillDraftId?: string;
  linkedObject?: { kind: string; id: string; label?: string };
  tCopilot: (key: string) => string;
  chatMessages: TerraUIMessage[];
  displayedMessages: TerraUIMessage[];
  status: 'streaming' | 'submitted' | 'ready' | 'error';
  editingQueuedMessageId?: string | null;
  updateQueuedMessage: (args: {
    chatId: string;
    queueItemId: string;
    message: TerraUIMessage;
  }) => void;
  clearBlockedAutoDrainForQueueItem: (queueItemId: string) => void;
  onQueuedMessageEditComplete?: () => void;
  hasPendingHITLReview: boolean;
  enqueueMessage: (args: { chatId: string; message: TerraUIMessage }) => void;
  handleAbort: (args?: { pauseQueueDrain?: boolean }) => Promise<void>;
  setBusyFollowUps: React.Dispatch<React.SetStateAction<TerraUIMessage[]>>;
  pauseQueueDrainAfterAbortRef: React.RefObject<boolean>;
  cancelScheduledRecovery: () => void;
  intentionalAbortRef: React.RefObject<boolean>;
  getChatExecuteUrl: () => string;
  sendUserMessageWithResult: (
    userMessage: TerraUIMessage,
    options?: { onPending?: () => void },
  ) => Promise<boolean>;
  sendErrorRef: React.RefObject<Error | undefined>;
  streamError: Error | undefined;
  clearNeoResumeSuppression: (chatId: string) => void;
  clearComposerLoadingSuppression: (chatId: string) => void;
}): Promise<boolean> {
  prepareChatUserTurn({
    chatId,
    clearNeoResumeSuppression,
    clearComposerLoadingSuppression,
  });

  if (isApplyingBranch && !branchFromMessageId) {
    return false;
  }

  const isInlineBranchEdit =
    Boolean(branchFromMessageId) && activeBranchEditSource?.kind === 'composer';
  let optimisticFollowUpId: string | undefined;

  try {
    const {
      content,
      originalUserText,
      mentions,
      conversationAttachmentsPart,
      messageParts,
      clearSentComposerContext,
      preservedBranchAttachmentCount,
    } = buildChatInstanceOutgoingParts({
      message,
      activeBranchEditSource,
      inlinePositionedMentions,
      chatId,
      isInlineBranchEdit,
      isChatWindowActive,
      selectedMentions,
      clearMentions,
      getMentionsFromEditor,
      chatComponentRef,
      updateChatTab,
      skillDraftId,
      linkedObject,
      tCopilot,
    });

    if (
      !content &&
      mentions.length === 0 &&
      !conversationAttachmentsPart &&
      preservedBranchAttachmentCount === 0
    ) {
      toasterApi.error({
        title: tCopilot('sendMessageErrorTitle'),
        description: tCopilot('sendMessageEmptyDescription'),
      });

      return false;
    }

    const pendingAskUser = extractPendingAskUserInteraction(chatMessages);
    const unansweredAskUser =
      pendingAskUser && pendingAskUser.selectedValue === undefined
        ? pendingAskUser.dataMessage
        : null;
    const askUserResponseText = originalUserText.trim();
    const isAnsweringAskUser =
      unansweredAskUser !== null && askUserResponseText.length > 0;
    const outgoingMetadata = metadataForOutgoingUserMessage({
      branchFromMessageId,
      isAnsweringAskUser,
      askUserResponseText,
      unansweredAskUser,
      liveMessages: chatMessages,
      status,
    });
    const userMessage: TerraUIMessage = {
      id: uuidv4(),
      role: 'user',
      parts: messageParts,
      ...(outgoingMetadata ? { metadata: outgoingMetadata } : {}),
    };

    if (editingQueuedMessageId) {
      updateQueuedMessage({
        chatId,
        queueItemId: editingQueuedMessageId,
        message: userMessage,
      });
      clearBlockedAutoDrainForQueueItem(editingQueuedMessageId);
      onQueuedMessageEditComplete?.();
      clearSentComposerContext();

      return true;
    }

    const shouldQueueForPendingHITL =
      hasPendingHITLReview && !branchFromMessageId && !isAnsweringAskUser;

    if (shouldQueueForPendingHITL) {
      enqueueMessage({
        chatId,
        message: userMessage,
      });
      clearSentComposerContext();

      return true;
    }

    if (
      (branchFromMessageId || isAnsweringAskUser) &&
      isActiveChatInstanceStatus(status)
    ) {
      await handleAbort({ pauseQueueDrain: false });
    }

    const isBusyComposerFollowUp =
      !branchFromMessageId &&
      !isAnsweringAskUser &&
      (status === 'streaming' || status === 'submitted');

    const sentAfterMessages = displayedMessages.filter(
      (msg) => msg.id !== userMessage.id,
    );
    const rememberSendPlacement = () =>
      rememberUserMessagePlacement({
        chatId,
        messageId: userMessage.id,
        ...userMessagePlacementForSend({
          predecessors: sentAfterMessages,
          liveMessages: chatMessages,
        }),
      });

    if (isBusyComposerFollowUp) {
      rememberSendPlacement();
    }

    rememberBusyCopilotFollowUp({
      chatId,
      message: userMessage,
    });
    setBusyFollowUps((current) => [...current, userMessage]);
    optimisticFollowUpId = userMessage.id;

    clearSentComposerContext();

    requestAnimationFrame(() => {
      chatComponentRef?.current?.scrollToBottom();
    });

    pauseQueueDrainAfterAbortRef.current = false;
    cancelScheduledRecovery();
    intentionalAbortRef.current = false;

    if (isBusyComposerFollowUp) {
      const followUpOutcome = await prepareStreamingCopilotFollowUpSend({
        url: getChatExecuteUrl(),
        chatId,
        body: {
          id: chatId,
          message: userMessage,
          ...getCopilotInvokeBodyExtras({
            chatId,
            getConfig: () =>
              useChatInstancesStore.getState().configs.get(chatId),
            consumePendingSettingsRefresh: true,
          }),
        },
      });

      if (followUpOutcome === 'pending') {
        return true;
      }

      if (followUpOutcome === 'rejected') {
        setBusyFollowUps((current) =>
          current.filter((msg) => msg.id !== userMessage.id),
        );
        forgetUserMessagePlacement({ chatId, messageId: userMessage.id });
        forgetBusyCopilotFollowUp({ chatId, messageId: userMessage.id });
        toasterApi.error({
          title: tCopilot('sendMessageErrorTitle'),
          description: tCopilot('sendMessageRetryDescription'),
        });

        return false;
      }

      await releaseOpenCopilotStream({
        chatId,
        markResumeStarted: 'before-stop',
      });
    }

    const sendSucceeded = await sendUserMessageWithResult(userMessage, {
      onPending: rememberSendPlacement,
    });

    if (!sendSucceeded) {
      discardCachedCopilotPromoteResponse({
        chatId,
        messageId: userMessage.id,
      });
      setBusyFollowUps((current) =>
        current.filter((msg) => msg.id !== userMessage.id),
      );
      forgetUserMessagePlacement({ chatId, messageId: userMessage.id });
      forgetBusyCopilotFollowUp({ chatId, messageId: userMessage.id });
      const sendFailure = sendErrorRef.current ?? streamError;
      const chatStatus = useChatInstancesStore.getState().getChatStatus(chatId);

      if (
        (sendFailure && isRecoverableStreamError(sendFailure)) ||
        chatStatus === 'error'
      ) {
        return false;
      }

      toasterApi.error({
        title: tCopilot('sendMessageErrorTitle'),
        description: tCopilot('sendMessageRetryDescription'),
      });

      return false;
    }

    return true;
  } catch (error) {
    if (optimisticFollowUpId) {
      discardCachedCopilotPromoteResponse({
        chatId,
        messageId: optimisticFollowUpId,
      });
      setBusyFollowUps((current) =>
        current.filter((msg) => msg.id !== optimisticFollowUpId),
      );
      forgetUserMessagePlacement({
        chatId,
        messageId: optimisticFollowUpId,
      });
      forgetBusyCopilotFollowUp({
        chatId,
        messageId: optimisticFollowUpId,
      });
    }

    intentionalAbortRef.current = false;
    console.error('Error sending message:', error);
    toasterApi.error({
      title: tCopilot('sendMessageErrorTitle'),
      description: tCopilot('sendMessageRetryDescription'),
    });

    return false;
  }
}
